/**
 * Shared beat-callback dispatch for both Conductor implementations.
 * Tracks the finest subdivision (sixteenths) and fires each subscriber when
 * musical time crosses onto its grid. Callbacks fire from the render loop
 * (Conductor.tick), never from their own timers — spec 5.1.
 */
import type { Unsubscribe } from "../core/eventBus";

const FINEST = 4; // sixteenths

export class BeatDispatcher {
  private lastFired = -1;
  private subs: Array<{ subdivision: number; cb: (beatIndex: number) => void }> = [];

  /** Fire every grid point crossed up to (and including) the current beat. */
  dispatch(beat: number): void {
    const idx = Math.floor(beat * FINEST);
    while (this.lastFired < idx) {
      this.lastFired++;
      for (const s of this.subs) {
        if (this.lastFired % (FINEST / s.subdivision) === 0) {
          s.cb(this.lastFired / FINEST);
        }
      }
    }
  }

  reset(): void {
    this.lastFired = -1;
  }

  /**
   * Resume dispatch from a mid-song position without back-firing history.
   * Grid points at or after `beat` still fire (a restart at beat 0 re-fires
   * the downbeat).
   */
  resetTo(beat: number): void {
    this.lastFired = Math.max(-1, Math.ceil(beat * FINEST) - 1);
  }

  on(subdivision: number, cb: (beatIndex: number) => void): Unsubscribe {
    const entry = { subdivision, cb };
    this.subs.push(entry);
    return () => {
      const i = this.subs.indexOf(entry);
      if (i >= 0) this.subs.splice(i, 1);
    };
  }

  clear(): void {
    this.subs.length = 0;
  }
}
